/**
 * CONSAI Business App - AI Search Bar Component
 * Phase 4: Keyword search + AI commands (Premium)
 * Voice input via Sarvam AI Saaras v3 STT (all Indian languages)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  Search, Mic, MicOff, Bot, X, Users, Package, Receipt, ShoppingCart,
  BookOpen, ArrowRight, Sparkles, Loader2, ChevronDown, Globe
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'Customers': Users,
  'Inventory': Package,
  'Invoices': Receipt,
  'Sales': ShoppingCart,
  'Credit': BookOpen
};

// All Sarvam Saaras v3 supported languages
const SARVAM_LANGUAGES = [
  { code: 'unknown', label: 'Auto Detect', native: '' },
  { code: 'hi-IN', label: 'Hindi', native: 'हिन्दी' },
  { code: 'en-IN', label: 'English', native: '' },
  { code: 'bn-IN', label: 'Bengali', native: 'বাংলা' },
  { code: 'ta-IN', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te-IN', label: 'Telugu', native: 'తెలుగు' },
  { code: 'mr-IN', label: 'Marathi', native: 'मराठी' },
  { code: 'gu-IN', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'kn-IN', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml-IN', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'pa-IN', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'od-IN', label: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'as-IN', label: 'Assamese', native: 'অসমীয়া' },
  { code: 'ur-IN', label: 'Urdu', native: 'اردو' },
  { code: 'ne-IN', label: 'Nepali', native: 'नेपाली' },
  { code: 'kok-IN', label: 'Konkani', native: 'कोंकणी' },
  { code: 'ks-IN', label: 'Kashmiri', native: 'कॉशुर' },
  { code: 'sd-IN', label: 'Sindhi', native: 'سنڌي' },
  { code: 'sa-IN', label: 'Sanskrit', native: 'संस्कृतम्' },
  { code: 'sat-IN', label: 'Santali', native: 'ᱥᱟᱱᱛᱟᱲᱤ' },
  { code: 'mni-IN', label: 'Manipuri', native: 'মণিপুরী' },
  { code: 'brx-IN', label: 'Bodo', native: 'बड़ो' },
  { code: 'mai-IN', label: 'Maithili', native: 'मैथिली' },
  { code: 'doi-IN', label: 'Dogri', native: 'डोगरी' },
];

const RECORD_DURATION_MS = 5000; // 5 seconds auto-stop

const SearchBar = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isAIMode, setIsAIMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [aiResponse, setAIResponse] = useState(null);
  const [aiUsage, setAIUsage] = useState({ used: 0, limit: 50, remaining: 50 });
  
  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceLang, setVoiceLang] = useState('unknown');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [recordProgress, setRecordProgress] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const progressRef = useRef(null);
  
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  
  const isPremium = user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';

  useEffect(() => {
    if (isPremium) fetchAIUsage();
  }, [isPremium]);

  const fetchAIUsage = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-search/usage`, { withCredentials: true });
      setAIUsage(res.data);
    } catch (err) { /* ignore */ }
  };

  const performKeywordSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) { setResults([]); setSuggestions([]); return; }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-search/keyword`, 
        { query: searchQuery, mode: 'keyword' }, { withCredentials: true });
      setResults(res.data.results || []);
      setSuggestions(res.data.suggestions || []);
    } catch (err) { console.error('Search error:', err); }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAIMode && query.length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => performKeywordSearch(query), 300);
    } else if (query.length < 2) { setResults([]); setSuggestions([]); }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, isAIMode, performKeywordSearch]);

  const handleAICommand = async (textOverride) => {
    if (!isPremium) {
      toast.error(language === 'hi' ? 'AI commands Premium only' : 'AI commands are Premium only');
      return;
    }
    const commandText = textOverride || query;
    if (!commandText.trim()) return;
    
    // Clear search box immediately for next entry
    setQuery('');
    setIsLoading(true);
    setAIResponse(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai-search/ai-command`,
        { query: commandText, mode: 'ai' }, { withCredentials: true });
      const data = res.data;
      setAIResponse(data);
      if (data.data?.remaining !== undefined) {
        setAIUsage(prev => ({ ...prev, remaining: data.data.remaining, used: 50 - data.data.remaining }));
      }
      if (data.action_type === 'navigate' && data.navigation_url) {
        setTimeout(() => { navigate(data.navigation_url); setIsOpen(false); }, 500);
      }
      if (data.action_type === 'action' && data.success) toast.success(data.message);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'AI command failed';
      setAIResponse({ success: false, action_type: 'error', message: errorMsg });
      if (err.response?.status === 403) toast.error('Upgrade to Premium');
    }
    setIsLoading(false);
  };

  // Stop recording helper
  const stopRecording = useCallback(() => {
    if (recordTimerRef.current) { clearTimeout(recordTimerRef.current); recordTimerRef.current = null; }
    if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
    setRecordProgress(0);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  }, []);

  // Voice input: record for 5 seconds, auto-stop, APPEND text to query
  const toggleVoiceInput = async () => {
    if (isListening) { stopRecording(); return; }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        
        const blobType = mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        
        if (blob.size < 100) {
          toast.error(language === 'hi' ? 'Audio record nahi hua' : 'No audio recorded');
          return;
        }
        
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'voice.webm');
          formData.append('language_code', voiceLang);
          
          const res = await axios.post(
            `${API_URL}/api/ai-search/voice-to-text`, formData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          
          const text = res.data.text;
          if (text && text.trim()) {
            // APPEND to existing query (don't replace)
            setQuery(prev => prev ? (prev.trim() + ' ' + text.trim()) : text.trim());
            setIsOpen(true);
            toast.success(
              language === 'hi' ? `"${text}"` : `Recognized: "${text}"`,
              { duration: 2000 }
            );
          } else {
            toast.error(language === 'hi' ? 'Kuch sunaai nahi diya' : 'Could not recognize speech. Try again.');
          }
        } catch (err) {
          console.error('Voice-to-text error:', err);
          toast.error(language === 'hi' ? 'Awaaz pehchaan fail' : 'Voice recognition failed. Try again.');
        }
        setIsTranscribing(false);
      };
      
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsListening(true);
      setRecordProgress(0);
      
      // Progress bar updates every 100ms
      let elapsed = 0;
      progressRef.current = setInterval(() => {
        elapsed += 100;
        setRecordProgress(Math.min((elapsed / RECORD_DURATION_MS) * 100, 100));
      }, 100);
      
      // Auto-stop after 5 seconds
      recordTimerRef.current = setTimeout(() => {
        stopRecording();
      }, RECORD_DURATION_MS);
      
      toast.info(
        language === 'hi' ? 'Boliye... (5 sec recording)' : 'Listening for 5 seconds...',
        { duration: 2000 }
      );
      
    } catch (err) {
      console.error('Microphone access error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error(language === 'hi' ? 'Microphone access dein' : 'Please allow microphone access');
      } else {
        toast.error(language === 'hi' ? 'Mic available nahi' : 'Microphone not available');
      }
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const handleResultClick = (result) => {
    if (result.url) { navigate(result.url); setIsOpen(false); setQuery(''); }
  };
  const handleSuggestionClick = (suggestion) => {
    navigate(suggestion.url); setIsOpen(false); setQuery('');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false); setShowLangPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isAIMode) { e.preventDefault(); handleAICommand(); }
  };

  const selectedLang = SARVAM_LANGUAGES.find(l => l.code === voiceLang) || SARVAM_LANGUAGES[0];

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl lg:max-w-full mx-auto" data-testid="ai-search-bar">
      {/* Search Input Bar */}
      <div className={`flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-xl border-2 transition-all ${
        isAIMode ? 'border-purple-500 bg-purple-50' : 'border-slate-200 bg-white'
      } ${isOpen ? 'shadow-lg' : 'shadow-sm'}`}>
        <Search className={`h-4 w-4 sm:h-5 sm:w-5 ml-1 sm:ml-2 shrink-0 ${isAIMode ? 'text-purple-500' : 'text-slate-400'}`} />
        
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setAIResponse(null); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={isAIMode 
            ? (language === 'hi' ? 'AI command type/bole...' : 'Type or speak AI command...')
            : (language === 'hi' ? 'Search...' : 'Search customers, stock, sales...')
          }
          className="border-0 focus-visible:ring-0 bg-transparent text-sm sm:text-base min-w-0"
          data-testid="search-input"
        />
        
        {/* Language Picker */}
        <div className="relative shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLangPicker(!showLangPicker)}
            className="h-7 sm:h-8 px-1.5 sm:px-2 text-slate-500 hover:text-slate-700 gap-0.5"
            data-testid="voice-lang-picker-btn"
            title="Voice language"
          >
            <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="text-[10px] sm:text-xs hidden sm:inline max-w-[50px] truncate">
              {selectedLang.code === 'unknown' ? 'Auto' : selectedLang.label}
            </span>
            <ChevronDown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
          </Button>
          
          {showLangPicker && (
            <div className="absolute right-0 top-full mt-1 w-52 sm:w-56 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl z-[60]" data-testid="voice-lang-dropdown">
              {SARVAM_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setVoiceLang(lang.code); setShowLangPicker(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex justify-between items-center ${
                    voiceLang === lang.code ? 'bg-purple-50 text-purple-700 font-medium' : 'text-slate-700'
                  }`}
                  data-testid={`voice-lang-${lang.code}`}
                >
                  <span>{lang.label}</span>
                  {lang.native && <span className="text-xs text-slate-400">{lang.native}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Voice Input Button with progress ring */}
        <div className="relative shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleVoiceInput}
            disabled={isTranscribing}
            className={`h-8 w-8 sm:h-9 sm:w-9 ${
              isListening
                ? 'bg-red-100 text-red-600'
                : isTranscribing
                  ? 'bg-yellow-100 text-yellow-600'
                  : 'text-slate-500'
            }`}
            data-testid="voice-input-btn"
          >
            {isTranscribing ? (
              <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
            ) : isListening ? (
              <MicOff className="h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <Mic className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </Button>
          {/* Recording progress ring */}
          {isListening && (
            <svg className="absolute inset-0 -rotate-90 pointer-events-none" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" stroke="#fca5a5" strokeWidth="2" />
              <circle cx="18" cy="18" r="16" fill="none" stroke="#dc2626" strokeWidth="2.5"
                strokeDasharray={`${recordProgress} ${100 - recordProgress}`}
                strokeLinecap="round" className="transition-all duration-100"
              />
            </svg>
          )}
        </div>
        
        {/* AI Mode Toggle - smaller on mobile */}
        {isPremium && (
          <Button
            variant={isAIMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setIsAIMode(!isAIMode); setResults([]); setSuggestions([]); setAIResponse(null); }}
            className={`shrink-0 h-6 w-6 sm:h-8 sm:w-auto sm:px-3 p-0 sm:p-2 text-[10px] sm:text-sm ${isAIMode ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
            data-testid="ai-mode-toggle"
          >
            <Bot className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">AI</span>
          </Button>
        )}
        
        {/* Submit button - smaller on mobile */}
        {isAIMode && query.trim() && (
          <Button
            onClick={() => handleAICommand()}
            disabled={isLoading}
            className="shrink-0 h-6 w-6 sm:h-8 sm:w-8 p-0 bg-purple-600 hover:bg-purple-700"
            data-testid="ai-submit-btn"
          >
            {isLoading ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />}
          </Button>
        )}
        
        {/* Clear button */}
        {query && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setQuery(''); setResults([]); setSuggestions([]); setAIResponse(null); }}
            className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400 shrink-0"
          >
            <X className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        )}
      </div>

      {/* AI Usage indicator */}
      {isAIMode && isPremium && (
        <div className="absolute right-2 -bottom-5 text-[10px] sm:text-xs text-purple-600">
          AI: {aiUsage.remaining}/{aiUsage.limit} remaining
        </div>
      )}

      {/* Dropdown Results */}
      {isOpen && (query.length >= 2 || aiResponse) && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 shadow-xl border-slate-200 max-h-[70vh] overflow-y-auto">
          <CardContent className="p-3">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            )}

            {aiResponse && !isLoading && (
              <div className={`p-4 rounded-lg mb-3 ${
                aiResponse.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  <Sparkles className={`h-5 w-5 mt-0.5 ${aiResponse.success ? 'text-green-600' : 'text-red-600'}`} />
                  <div className="flex-1">
                    <p className={`text-sm whitespace-pre-line ${
                      aiResponse.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {aiResponse.message}
                    </p>
                    {aiResponse.navigation_url && (
                      <Button variant="link" size="sm"
                        onClick={() => { navigate(aiResponse.navigation_url); setIsOpen(false); }}
                        className="p-0 h-auto mt-2 text-purple-600"
                      >Open page →</Button>
                    )}
                    {aiResponse.data?.options && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {aiResponse.data.options.map((opt, i) => (
                          <Button key={i} variant="outline" size="sm" onClick={() => setQuery(opt)}>{opt}</Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {suggestions.length > 0 && !isAIMode && (
              <div className="mb-3">
                <p className="text-xs text-slate-500 mb-2">Quick Navigation</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((sug, idx) => (
                    <Button key={idx} variant="outline" size="sm"
                      onClick={() => handleSuggestionClick(sug)} className="text-xs"
                    >{sug.label}</Button>
                  ))}
                </div>
              </div>
            )}

            {!isAIMode && results.length > 0 && (
              <div className="space-y-3">
                {results.map((category, idx) => {
                  const IconComponent = CATEGORY_ICONS[category.category] || Package;
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className="h-4 w-4 text-slate-500" />
                        <span className="text-xs font-medium text-slate-500 uppercase">{category.category}</span>
                      </div>
                      <div className="space-y-1">
                        {category.items.map((item, iIdx) => (
                          <button key={iIdx}
                            onClick={() => handleResultClick(item)}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 transition-colors text-left"
                            data-testid={`search-result-${item.id}`}
                          >
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{item.title}</p>
                              <p className="text-xs text-slate-500">{item.subtitle}</p>
                            </div>
                            {item.badge && (
                              <Badge variant={item.badge_type === 'credit' ? 'destructive' : item.badge_type === 'warning' ? 'warning' : 'secondary'} className="text-xs">
                                {item.badge}
                              </Badge>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!isLoading && !aiResponse && results.length === 0 && query.length >= 2 && !isAIMode && (
              <div className="text-center py-6 text-slate-500 text-sm">No results found</div>
            )}

            {!isAIMode && !isPremium && query.length >= 2 && (
              <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700">
                  <Sparkles className="h-3 w-3 inline mr-1" />
                  Upgrade to Premium for AI commands like "Add 10 Maggi to stock"
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchBar;
